/**
 * Les **comptes d'un guichet** — guichetiers et responsables (V89, 2026-10-01).
 *
 * Aucun chemin de production ne créait un guichetier : on l'écrivait à la main en
 * SQL, sans trace d'audit, et aucune route ne disait qui tient quel comptoir. Ce
 * module branche la lecture (`OUTLET_OPERATOR_VIEW`) et les trois gestes :
 * créer (`OUTLET_OPERATOR_CREATE`), effacer le code (`OUTLET_OPERATOR_PIN_RESET`)
 * et couper les sessions (`OUTLET_DEVICE_REVOKE`, qui coupe des sessions depuis
 * qu'il n'y a plus de terminal lié — V88).
 *
 * **Le compte naît SANS code secret.** Le guichetier le choisit à sa première
 * connexion, après un code SMS sur le numéro de sa fiche : aucun code ne voyage.
 */

import type { ApiClient } from './api-client.js';
import type { ActionOutcome } from './liquidity.js';

/** La racine des lectures du réseau. */
export const networkPath = '/api/backoffice/network';

/** La route des actions du socle. */
export const operatorActionsPath = '/api/backoffice/actions';

/** Le motif des lectures de cet écran. */
export const operatorsReason = 'gestion-des-comptes-du-guichet';

export const createOperatorAction = 'OUTLET_OPERATOR_CREATE';
export const resetOperatorPinAction = 'OUTLET_OPERATOR_PIN_RESET';
export const cutOperatorSessionsAction = 'OUTLET_DEVICE_REVOKE';

/** Les rôles qu'un formulaire de réseau peut donner. Un agent ne se crée pas ici. */
export const counterRoles = ['OPERATEUR', 'RESPONSABLE'] as const;

/** Un compte du guichet, tel que le serveur le rend. */
export interface OutletOperator {
  readonly operatorId: string;
  readonly fullName: string;
  readonly msisdn: string;
  readonly role: string;
  readonly status: string;
  /** Vrai tant que le guichetier n'a pas choisi son code secret. */
  readonly awaitsPin: boolean;
  /** La fin du verrou, ou `null` quand le compte n'est pas verrouillé. */
  readonly lockedUntil: string | null;
}

/** Lit les comptes de ce guichet. */
export async function fetchOutletOperators(
  client: ApiClient,
  outletId: string,
  reason: string = operatorsReason,
): Promise<readonly OutletOperator[]> {
  const parametres = new URLSearchParams({ reason });
  return client.request<readonly OutletOperator[]>(
    `${networkPath}/outlets/${encodeURIComponent(outletId)}/operators?${parametres.toString()}`,
  );
}

/** Crée un guichetier — sans code secret. */
export async function createOutletOperator(
  client: ApiClient,
  demande: {
    readonly outletCode: string;
    readonly fullName: string;
    readonly msisdn: string;
    readonly role: string;
    readonly reason: string;
  },
): Promise<ActionOutcome> {
  return agir(client, createOperatorAction, demande.outletCode, demande.reason, {
    fullName: demande.fullName,
    msisdn: demande.msisdn,
    role: demande.role,
  });
}

/** Efface le code secret d'un guichetier, et coupe ses sessions. */
export async function resetOperatorPin(
  client: ApiClient,
  operatorId: string,
  reason: string,
): Promise<ActionOutcome> {
  return agir(client, resetOperatorPinAction, operatorId, reason, {});
}

/** Coupe toutes les sessions d'un guichetier. */
export async function cutOperatorSessions(
  client: ApiClient,
  operatorId: string,
  reason: string,
): Promise<ActionOutcome> {
  return agir(client, cutOperatorSessionsAction, operatorId, reason, {});
}

async function agir(
  client: ApiClient,
  action: string,
  entityRef: string,
  reason: string,
  payload: Readonly<Record<string, string>>,
): Promise<ActionOutcome> {
  return client.request<ActionOutcome>(`${operatorActionsPath}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entityRef, reason, payload }),
  });
}
